import { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

const getUserSession = () => {
  try {
    const raw = localStorage.getItem('tk_fashion_user_session');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const parseJsonSafe = async (res) => {
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const TYPE_CONFIG = {
  order_placed:    { icon: '🛍️', color: '#2563eb', bg: '#dbeafe' },
  order_status:    { icon: '📦', color: '#7c3aed', bg: '#ede9fe' },
  payment_success: { icon: '✅', color: '#16a34a', bg: '#dcfce7' },
  payment_failed:  { icon: '❌', color: '#dc2626', bg: '#fee2e2' },
  ticket_message:  { icon: '💬', color: '#0891b2', bg: '#cffafe' },
  ticket_closed:   { icon: '🔒', color: '#6b7280', bg: '#f3f4f6' },
  ticket_assigned: { icon: '🛡️', color: '#7c3aed', bg: '#ede9fe' },
  new_ticket:      { icon: '🎫', color: '#d97706', bg: '#fef3c7' },
  new_order:       { icon: '💳', color: '#16a34a', bg: '#dcfce7' },
  broadcast_announcement: { icon: '📢', color: '#1d4ed8', bg: '#dbeafe' },
};

const getConfig = (type) => TYPE_CONFIG[type] || { icon: '🔔', color: '#6b7280', bg: '#f3f4f6' };

export default function NotificationsPage({ onNavigate }) {
  const session = getUserSession();
  const token = session?.token;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/my?limit=60`, {
        headers: authHeaders(token),
      });
      const data = await parseJsonSafe(res);
      if (res.ok) {
        setNotifications(data.notifications || []);
        setUnread(data.unread || 0);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markOneRead = async (notif) => {
    if (notif.readAt) {
      // Already read — navigate if link present
      if (notif.link && onNavigate) onNavigate(notif.link);
      return;
    }
    try {
      await fetch(`${API_BASE_URL}/api/notifications/my/${notif._id}/read`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      setNotifications((prev) =>
        prev.map((n) => n._id === notif._id ? { ...n, readAt: new Date().toISOString() } : n)
      );
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* ignore */ }
    if (notif.link && onNavigate) onNavigate(notif.link);
  };

  const markAllRead = async () => {
    if (markingAll || unread === 0) return;
    setMarkingAll(true);
    try {
      await fetch(`${API_BASE_URL}/api/notifications/my/read-all`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
      setUnread(0);
    } catch { /* ignore */ }
    finally { setMarkingAll(false); }
  };

  const deleteOne = async (notif) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/my/${notif._id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) return;
      setNotifications((prev) => prev.filter((n) => n._id !== notif._id));
      if (!notif.readAt) setUnread((u) => Math.max(0, u - 1));
    } catch {
      // ignore
    }
  };

  if (!token) {
    return (
      <div className="notif-page">
        <div className="notif-empty">
          <div className="notif-empty-icon">🔔</div>
          <h2>Notifications</h2>
          <p>Please log in to see your notifications.</p>
        </div>
      </div>
    );
  }

  const filtered = filter === 'unread'
    ? notifications.filter((n) => !n.readAt)
    : notifications;

  return (
    <div className="notif-page">
      {/* Header */}
      <div className="notif-header">
        <div className="notif-header-left">
          <h2 className="notif-title">Notifications</h2>
          <p className="notif-subtitle">Stay updated on your orders, payments and support</p>
        </div>
        {unread > 0 && (
          <button
            type="button"
            className="notif-mark-all-btn"
            onClick={markAllRead}
            disabled={markingAll}
          >
            {markingAll ? 'Marking…' : '✓ Mark all read'}
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="notif-filter-bar">
        {['all', 'unread'].map((f) => (
          <button
            key={f}
            type="button"
            className={`notif-filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : 'Unread'}
            {f === 'unread' && unread > 0 && (
              <span className="notif-filter-count">{unread}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="notif-loading">
          <div className="notif-spinner" />
          <p>Loading notifications…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="notif-empty">
          <div className="notif-empty-icon">🔔</div>
          <h3>{filter === 'unread' ? 'All caught up!' : 'No notifications yet'}</h3>
          <p>{filter === 'unread' ? 'You have no unread notifications.' : 'You\'ll see order updates, support replies and more here.'}</p>
        </div>
      ) : (
        <div className="notif-list">
          {filtered.map((n) => {
            const cfg = getConfig(n.type);
            return (
              <div
                key={n._id}
                role="button"
                tabIndex={0}
                className={`notif-item ${!n.readAt ? 'unread' : ''}`}
                onClick={() => markOneRead(n)}
                onKeyDown={(e) => e.key === 'Enter' && markOneRead(n)}
              >
                <div
                  className="notif-item-icon"
                  style={{ background: cfg.bg, color: cfg.color }}
                >
                  {cfg.icon}
                </div>
                <div className="notif-item-body">
                  <div className="notif-item-title">{n.title}</div>
                  <div className="notif-item-text">{n.body}</div>
                  <div className="notif-item-time">{formatTime(n.createdAt)}</div>
                </div>
                <div className="notif-item-actions">
                  <button
                    type="button"
                    className="notif-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteOne(n);
                    }}
                    aria-label="Delete notification"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
                {!n.readAt && <div className="notif-unread-dot" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
